import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Form, Spinner } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function SalesPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState('generate'); // generate, generating, editing
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Content data
  const [salesPage, setSalesPage] = useState('');
  const [savedContent, setSavedContent] = useState(null);
  const [productName, setProductName] = useState('');
  const [targetMarket, setTargetMarket] = useState('');

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Get target market for personalized title
    try {
      const marketResponse = await fetch('/api/target-market/primary', {
        credentials: 'include'
      });

      if (marketResponse.ok) {
        const marketData = await marketResponse.json();
        if (marketData.primary_niche) {
          setTargetMarket(marketData.primary_niche.niche_name || '');
        }
      }
    } catch (err) {
      console.error('Error loading target market:', err);
    }

    // Get product name from pricing data
    try {
      const pricingResponse = await fetch('/api/business/data/pricing_data', {
        credentials: 'include'
      });

      if (pricingResponse.ok) {
        const pricingData = await pricingResponse.json();
        if (pricingData.data && pricingData.data.product_name) {
          setProductName(pricingData.data.product_name);
        }
      }
    } catch (err) {
      console.error('Error loading product name:', err);
    }

    // Check if user has saved sales page
    try {
      const contentResponse = await fetch('/api/assets/sales-page/get', {
        credentials: 'include'
      });

      if (contentResponse.ok) {
        const contentData = await contentResponse.json();
        if (contentData.content) {
          setSavedContent(contentData.content);
          setSalesPage(contentData.content);
          setStage('editing');
        }
      }
    } catch (err) {
      console.error('Error loading saved data:', err);
    }

    setLoading(false);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setStage('generating');

    try {
      const response = await fetch('/api/assets/sales-page/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSalesPage(data.content);
        setStage('editing');
      } else {
        const errorData = await response.json();
        setError(`Failed to generate content: ${errorData.error || 'Unknown error'}`);
        setStage('generate');
      }
    } catch (err) {
      setError('Error generating content: ' + err.message);
      setStage('generate');
    }

    setGenerating(false);
  };

  const handleSave = async () => {
    if (!salesPage || !salesPage.trim()) {
      setError('Please enter your sales page content');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/assets/sales-page/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: salesPage })
      });

      if (response.ok) {
        setSavedContent(salesPage);
        // Show success message
        setError('');
        alert('Sales page saved successfully!');
      } else {
        const errorData = await response.json();
        setError(`Failed to save sales page: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError('Error saving sales page: ' + err.message);
    }

    setSaving(false);
  };

  const handleContinue = () => {
    navigate('/dashboard/assets-creation');
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="assets-creation" />
      <Row className="mb-4">
        <Col>
          <h1>Sales Page{targetMarket && ` for ${targetMarket}`}</h1>
          <p className="text-muted">
            Create a complete, ready-to-use long-form sales page for your core product.
          </p>
          {productName && (
            <Alert variant="info">
              <strong>Product:</strong> {productName}
            </Alert>
          )}
        </Col>
      </Row>

      {error && !error.includes('saved successfully') && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {/* Generate Stage */}
      {stage === 'generate' && (
        <Row>
          <Col md={8}>
            <Card>
              <Card.Body>
                <h4 className="mb-4">📝 Generate Complete Sales Page</h4>
                <p className="mb-4">
                  Our AI will create a comprehensive, conversion-optimized long-form sales page based on your product features, benefits, USP, and business context.
                </p>

                <Alert variant="info" className="mb-4">
                  <strong>What you'll get - 17 complete sections:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Pre-headline, headline, and compelling subheadline</li>
                    <li>Opening hook with story and pain agitation</li>
                    <li>Enemy identification and solution introduction</li>
                    <li>Complete "How it works" framework</li>
                    <li>All your features woven throughout with emotional benefits</li>
                    <li>Social proof, value stack, and pricing reveal</li>
                    <li>Guarantee, urgency, FAQ, and multiple CTAs</li>
                  </ul>
                </Alert>

                <div className="d-grid">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleGenerate}
                    disabled={generating}
                  >
                    {generating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Generating Complete Sales Page... (60-90 seconds)
                      </>
                    ) : (
                      'Generate Complete Sales Page →'
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="bg-light">
              <Card.Body>
                <h5 className="mb-3">💡 Sales Page Tips</h5>
                <ul className="small mb-0">
                  <li>Customize stories to your brand</li>
                  <li>Add real customer testimonials</li>
                  <li>Update pricing and guarantee details</li>
                  <li>Verify scarcity claims are accurate</li>
                  <li>Adapt FAQ to common objections</li>
                  <li>Add your brand voice throughout</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Generating Stage */}
      {stage === 'generating' && (
        <Row>
          <Col md={12}>
            <Card>
              <Card.Body className="text-center py-5">
                <Spinner animation="border" className="mb-3" />
                <h4>Generating Your Complete Sales Page...</h4>
                <p className="text-muted">
                  This will take about 60-90 seconds. We're weaving your features, benefits, USP, and offer into a comprehensive, conversion-optimized long-form sales page with full neuropersuasion.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Editing Stage */}
      {stage === 'editing' && (
        <>
          <Row className="mb-4">
            <Col md={12}>
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">✏️ Review & Edit Sales Page</h4>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleGenerate}
                      disabled={generating}
                    >
                      {generating ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Regenerating...
                        </>
                      ) : (
                        'Regenerate Sales Page'
                      )}
                    </Button>
                  </div>

                  <Alert variant="info" className="mb-4">
                    <strong>Review your AI-generated sales page below.</strong> This comprehensive long-form page includes all 17 sections. Edit directly or regenerate.
                  </Alert>

                  <Alert variant="secondary" className="mb-4">
                    <strong>This gives you a solid, professional sales page to start with.</strong>
                    <p className="mb-0 mt-2">
                      When you're ready to maximize conversions, the <strong>Command Center</strong> creates
                      hyper-personalized sales pages using your actual testimonials, case studies, founder story,
                      and advanced neuromarketing psychology - automatically A/B tested and optimized over time.
                    </p>
                  </Alert>

                  <Form.Group className="mb-4">
                    <Form.Label><strong>Complete Sales Page Copy:</strong></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={30}
                      value={salesPage || ''}
                      onChange={(e) => setSalesPage(e.target.value)}
                      style={{ fontFamily: 'monospace', fontSize: '14px' }}
                    />
                    <Form.Text className="text-muted">
                      This is your complete long-form sales page. Edit to match your brand voice, add real testimonials, and verify pricing details.
                    </Form.Text>
                  </Form.Group>

                  <div className="d-grid gap-2">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleSave}
                      disabled={saving || !salesPage || !salesPage.trim()}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        'Save Sales Page'
                      )}
                    </Button>

                    {savedContent && (
                      <Button
                        variant="outline-primary"
                        size="lg"
                        onClick={handleContinue}
                      >
                        Return to Dashboard →
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {savedContent && (
            <Row>
              <Col md={12}>
                <Alert variant="success">
                  <strong>✓ Sales Page Saved</strong><br />
                  Your comprehensive long-form sales page is saved. You can make additional edits here or return to the dashboard.
                </Alert>
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  );
}

export default SalesPage;
